import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { db, auth } from '../services/firebase'
import {
    collection,
    query,
    where,
    orderBy,
    getDocs,
    deleteDoc,
    doc,
    limit,
    startAfter
} from 'firebase/firestore'
import './HistoryPage.css'



const STATUS_FILTERS = [
    { value: 'approved', label: 'Verified' },
    { value: 'pending_approval', label: 'Pending Verification' },
    { value: '', label: 'All' }
]

function HistoryPage() {
    const [history, setHistory] = useState([])
    const [loading, setLoading] = useState(true)

    const [statusFilter, setStatusFilter] = useState('approved')  // Show approved by default
    const [dateRange, setDateRange] = useState('')
    const [deleteConfirm, setDeleteConfirm] = useState(null)

    useEffect(() => {
        const fetchHistory = async () => {
            if (!auth.currentUser) return

            setLoading(true)
            try {
                const q = query(
                    collection(db, 'identifications'),
                    where('userId', '==', auth.currentUser.uid),
                    orderBy('createdAt', 'desc'),
                    limit(20)
                )

                const snapshot = await getDocs(q)
                const items = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    // Flatten for UI compatibility
                    name: doc.data().manufacturer || doc.data().identifiedManufacturer || doc.data().inferred?.possibleManufacturers?.[0] || 'Unknown Item',
                    submittedAt: doc.data().createdAt?.toDate() || new Date(),
                    imageUrl: doc.data().mainImageUrl || doc.data().images?.[0]?.url
                }))
                setHistory(items)
            } catch (err) {
                console.error('Error fetching history:', err)
            } finally {
                setLoading(false)
            }
        }

        fetchHistory()
    }, [])

    const filteredHistory = history.filter(item => {
        // Filter by status
        if (statusFilter && item.status !== statusFilter) return false

        // Exclude rejected items
        if (item.status === 'rejected') return false
        return true
    })

    const handleDelete = async (id) => {
        try {
            await deleteDoc(doc(db, 'identifications', id))
            setHistory(prev => prev.filter(item => item.id !== id))
            setDeleteConfirm(null)
        } catch (err) {
            console.error('Delete failed:', err)
            alert('Failed to delete item.')
        }
    }

    const getConfidenceBadgeClass = (confidence) => {
        if (confidence >= 90) return 'high'
        if (confidence >= 70) return 'medium'
        return 'low'
    }

    if (loading) {
        return (
            <div className="history-page loading">
                <div className="loading-spinner large" />
                <p>Loading your collection...</p>
            </div>
        )
    }

    return (
        <div className="history-page">
            <div className="container">
                {/* Header */}
                <div className="page-header">
                    <h1>My Collection</h1>
                    <p>View and manage your past identifications</p>
                </div>

                {/* Filters */}
                <div className="filters-bar">
                    <div className="filter-group">
                        <label htmlFor="statusFilter" className="filter-label">Status</label>
                        <select
                            id="statusFilter"
                            className="filter-select"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            {STATUS_FILTERS.map(sf => (
                                <option key={sf.value} value={sf.value}>{sf.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group">
                        <label htmlFor="dateRange" className="filter-label">Date Range</label>
                        <select
                            id="dateRange"
                            className="filter-select"
                            value={dateRange}
                            onChange={(e) => setDateRange(e.target.value)}
                        >
                            <option value="">All Time</option>
                            <option value="7">Last 7 Days</option>
                            <option value="30">Last 30 Days</option>
                            <option value="90">Last 3 Months</option>
                        </select>
                    </div>


                </div>

                {/* History Grid */}
                {filteredHistory.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">📦</div>
                        <h2>No Items Yet</h2>
                        <p>Start identifying your vintage treasures!</p>
                        <Link to="/identify" className="btn btn-primary">Identify Your First Item</Link>
                    </div>
                ) : (
                    <>
                        <div className="history-grid">
                            {filteredHistory.map(item => (
                                <div key={item.id} className="history-card">
                                    <Link to={`/results/${item.id}`} className="card-image">
                                        <img
                                            src={item.imageUrl}
                                            alt={item.name}
                                            onError={(e) => e.target.src = 'https://via.placeholder.com/280x280?text=Image'}
                                        />
                                    </Link>

                                    <div className="card-content">
                                        <Link to={`/results/${item.id}`}>
                                            <h3 className="card-title">{item.name}</h3>
                                        </Link>

                                        <p className="card-date">
                                            Date Submitted: {new Date(item.submittedAt).toLocaleDateString()}
                                        </p>



                                        <p className="card-price">
                                            Status: <span className={`status-${item.status}`}>{item.status}</span>
                                        </p>

                                        <div className="card-actions">
                                            <Link to={`/results/${item.id}`} className="action-btn view">
                                                View Details
                                            </Link>
                                            <button
                                                className="action-btn delete"
                                                onClick={() => setDeleteConfirm(item.id)}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination */}
                        <div className="pagination">
                            <button className="page-btn" disabled>Previous</button>
                            <span className="page-info">1 2 3 ... 15</span>
                            <button className="page-btn">Next</button>
                        </div>
                    </>
                )}

                {/* Delete Confirmation Modal */}
                {deleteConfirm && (
                    <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
                        <div className="modal-content card-glass" onClick={e => e.stopPropagation()}>
                            <h2 className="modal-title">Delete Item?</h2>
                            <p>Are you sure you want to permanently delete this identification? This action cannot be undone.</p>
                            <div className="modal-actions">
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => setDeleteConfirm(null)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn btn-danger"
                                    onClick={() => handleDelete(deleteConfirm)}
                                >
                                    Delete Permanently
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default HistoryPage
